import React, { useState, useEffect } from 'react';
import { Title, Meta, Link } from 'react-head';
import { motion } from 'framer-motion';
import BlogHero from './components/BlogHero/BlogHero';
import CategoryFilter from './components/CategoryFilter/CategoryFilter';
import SearchBar from './components/SearchBar/SearchBar';
import BlogGrid from './components/BlogGrid/BlogGrid';
import BlogSidebar from './components/BlogSidebar/BlogSidebar';
import Pagination from './components/Pagination/Pagination';
import { blogPosts } from '../../../data/blogPosts';
import { categories } from '../../../data/categories';
import './Blog.css';

const Blog = () => {
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter posts by category and search
  useEffect(() => {
    let filtered = blogPosts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => 
        post.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory
      );
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredPosts(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [selectedCategory, searchQuery]);

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Title>Financial Insights & Expert Guides | Trinity Financing Blog</Title>
      <Meta name="description" content="Expert insights on trade finance, LPO financing, business capital, and global finance trends. Learn from our specialists and real success stories." />
      <Meta name="keywords" content="trade finance blog, business financing guides, LPO financing tips, financial insights, business funding advice, Trinity Financing" />
      <Meta name="robots" content="index, follow" />
      <Link rel="canonical" href="https://trinityfinancing.com/blog" />
      
      {/* Open Graph Tags */}
      <Meta property="og:type" content="website" />
      <Meta property="og:url" content="https://trinityfinancing.com/blog" />
      <Meta property="og:title" content="Financial Insights & Expert Guides | Trinity Financing Blog" />
      <Meta property="og:description" content="Expert insights on trade finance, LPO financing, business capital, and global finance trends." />
      <Meta property="og:image" content="https://trinityfinancing.com/blog-featured.jpg" />
      
      {/* Twitter Card */}
      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content="Financial Insights & Expert Guides | Trinity Financing Blog" />
      <Meta name="twitter:description" content="Expert insights on trade finance, LPO financing, and business capital." />

      {/* Page Content */}
      <div className="blog-page">
        <BlogHero />
        
        <div className="blog-container">
          <div className="blog-controls">
            <CategoryFilter 
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
            <SearchBar 
              onSearch={handleSearch}
              searchQuery={searchQuery}
            />
          </div>

          <div className="blog-content-wrapper">
            <div className="blog-main">
              {filteredPosts.length === 0 ? (
                <div className="no-results">
                  <h3>No posts found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <BlogGrid posts={currentPosts} />
                  </motion.div>

                  {totalPages > 1 && (
                    <Pagination 
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              )}
            </div>

            <aside className="blog-sidebar-wrapper">
              <BlogSidebar />
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;